import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Menu, X, MonitorSmartphone } from "lucide-react";

const productLinks = [
  {
    name: "Signulu",
    logo: "/assets/signuluone-lockup.png",
    desc: "Cloud-based eSignature software developed for the utmost convenience of individuals, professionals, and businesses.",
    href: "https://signulu.signuluone.com/",
  },
  {
    name: "DMS",
    logo: "/assets/logo-dms.webp",
    desc: "Intelligent secure document management for modern enterprises, with AES-256 encryption.",
    href: "https://dms.signuluone.com/",
  },
  {
    name: "QuikHyr AI",
    logo: "/assets/logo-quikhyr.svg",
    desc: "Unifies hiring workflows, intelligent data, and AI insights into one secure, cloud-native platform.",
    href: "https://quikhyr.ai/",
  },
  {
    name: "Attainica",
    logo: "/assets/logo-attainica.png",
    desc: "An efficacious staffing ERP system. A one-stop destination for all your staffing hassles.",
    href: "https://www.attainica.com/",
  },
];

const ProductsMenu = () => (
  <div className="relative group" data-testid="nav-products-dropdown">
    <a
      href="/our-products"
      data-testid="nav-products-dropdown-trigger"
      className="flex items-center gap-1 text-[15px] font-medium text-slate-800 hover:text-miti-blue transition-colors py-2"
    >
      Our Products
      <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />
    </a>
    <div className="fixed left-1/2 -translate-x-1/2 top-[76px] pt-3 opacity-0 invisible translate-y-3 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
      <div className="w-[920px] max-w-[94vw] rounded-2xl border border-slate-100 bg-white shadow-2xl shadow-slate-900/10 p-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {productLinks.map((p) => (
          <div
            key={p.name}
            data-testid={`nav-product-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex flex-col items-center text-center"
          >
            <div className="h-11 flex items-center justify-center">
              <img src={p.logo} alt={`${p.name} logo`} className="max-h-10 max-w-[150px] object-contain" />
            </div>
            <h4 className="mt-4 font-heading font-bold text-lg text-slate-900">{p.name}</h4>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed flex-1">{p.desc}</p>
            <a
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`nav-product-explore-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="mt-4 inline-flex items-center justify-center rounded-lg bg-miti-blue px-7 py-2.5 text-xs font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              Explore
            </a>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const techServices = [
  { label: "Generative AI Services", href: "/services/generative-ai" },
  { label: "Digital Transformation", href: "/services/digital-transformation" },
  { label: "Business Applications", href: "/services/business-applications" },
  { label: "Data and Intelligence", href: "/services/data-and-intelligence" },
  { label: "Apps and Infrastructure", href: "/services/apps-and-infrastructure" },
];

const ServicesMenu = () => (
  <div className="relative group" data-testid="nav-services-dropdown">
    <button
      data-testid="nav-services-dropdown-trigger"
      className="flex items-center gap-1 text-[15px] font-medium text-slate-800 hover:text-miti-blue transition-colors py-2"
    >
      Services
      <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
      <div className="w-64 rounded-xl border border-slate-100 bg-white/95 backdrop-blur-xl shadow-xl shadow-slate-900/5 py-2">
        <div className="relative group/tech">
          <a
            href="/services/technology-services"
            data-testid="nav-technology-services"
            className="flex items-center justify-between px-5 py-2.5 text-sm font-medium text-miti-blue hover:bg-slate-50 transition-colors"
          >
            Technology Services
            <ChevronRight size={14} />
          </a>
          <div className="absolute left-full -top-2 pl-2 opacity-0 invisible translate-x-2 group-hover/tech:opacity-100 group-hover/tech:visible group-hover/tech:translate-x-0 transition-all duration-300">
            <div className="w-64 rounded-xl border border-slate-100 bg-white/95 backdrop-blur-xl shadow-xl shadow-slate-900/5 py-2">
              {techServices.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  data-testid={`nav-service-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block px-5 py-2.5 text-sm text-slate-700 hover:text-miti-blue hover:bg-slate-50 transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
        <a
          href="/services/professional-services"
          data-testid="nav-professional-services"
          className="block px-5 py-2.5 text-sm text-slate-700 hover:text-miti-blue hover:bg-slate-50 transition-colors"
        >
          Professional Services
        </a>
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm"
          : "bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[76px]">
        <a href="/" data-testid="nav-logo-link" className="flex items-center shrink-0">
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          <a
            href="/"
            data-testid="nav-home"
            className={`text-[15px] pb-0.5 transition-colors ${
              pathname === "/"
                ? "font-semibold text-miti-blue border-b-2 border-miti-blue"
                : "font-medium text-slate-800 hover:text-miti-blue"
            }`}
          >
            Home
          </a>
          <a
            href="/about"
            data-testid="nav-about"
            className={`text-[15px] pb-0.5 transition-colors ${
              pathname === "/about"
                ? "font-semibold text-miti-blue border-b-2 border-miti-blue"
                : "font-medium text-slate-800 hover:text-miti-blue"
            }`}
          >
            About Us
          </a>
          <a
            href="/careers"
            data-testid="nav-careers"
            className={`text-[15px] pb-0.5 transition-colors ${
              pathname === "/careers"
                ? "font-semibold text-miti-blue border-b-2 border-miti-blue"
                : "font-medium text-slate-800 hover:text-miti-blue"
            }`}
          >
            Careers
          </a>
          <ProductsMenu />
          <ServicesMenu />
          {/* <a href="/blog" data-testid="nav-blog" className="text-[15px] font-medium text-slate-800 hover:text-miti-blue transition-colors">
            Blog
          </a> */}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/contact"
            data-testid="nav-contact-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-miti-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#005a96] hover:shadow-lg hover:shadow-miti-blue/25 transition-all duration-300"
          >
            Contact Us
            <MonitorSmartphone size={16} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-slate-800"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-slate-100 px-6 py-6 space-y-4">
          {[
            { label: "Home", href: "/" },
            { label: "About Us", href: "/about" },
            { label: "Careers", href: "/careers" },
            // { label: "Blog", href: "/blog" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className="block text-base font-medium text-slate-800"
            >
              {l.label}
            </a>
          ))}
          <div className="pt-2 border-t border-slate-100">
            <a
              href="/our-products"
              data-testid="nav-mobile-our-products"
              onClick={() => setOpen(false)}
              className="block text-xs font-semibold tracking-widest text-slate-400 mb-2 hover:text-miti-blue transition-colors"
            >
              OUR PRODUCTS
            </a>
            {productLinks.map((p) => (
              <a key={p.name} href={p.href} className="block py-1.5 text-sm text-slate-600">
                {p.name}
              </a>
            ))}
          </div>
          <a
            href="/contact"
            data-testid="nav-mobile-contact-button"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 rounded-lg bg-miti-blue px-5 py-2.5 text-sm font-semibold text-white"
          >
            Contact Us
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
